import { designMode } from '@/env';
import { api } from '@/api';
import router from '@/router';
import { getLocalToken, getState, removeLocalToken, saveLocalToken, toBoolean } from '@/utils';
import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import { readHasAdminAccess } from './getters';
import {
    commitAddNotification,
    commitRemoveNotification,
    commitSetLoggedIn,
    commitSetLogInError,
    commitSetSetupCompleted,
    commitSetToken,
    commitSetUserCompany,
    commitSetUserProfile,
} from './mutations';
import { AppNotification, MainState } from './state';
import { ISignup, IUserProfile } from '@/interfaces';
import { commitSetReportId, commitSetReports } from '../report/mutations';
import { Company } from '../admin';

type MainContext = ActionContext<MainState, State>;

export const actions = {
    async actionSignup(context: MainContext, payload: ISignup) {
        try {
            if (!toBoolean(designMode)) {
                await Promise.all([
                    api.signup(payload),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ]);
                await dispatchLogIn(context, { username: payload.user_email, password: payload.user_password });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
            await dispatchSignupError(context, error);
        }
    },
    async actionSignupError(context: MainContext, payload: AxiosError) {
        if (payload.response!.status === 400) {
            commitAddNotification(context, { content: payload.response!.data.detail, condition: 'error' });
        }
    },
    async actionGoogleSignup(context: MainContext, payload: string) {
        try {
            if (!toBoolean(designMode)) {
                await Promise.all([
                    api.googleSignup(payload),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ]);
                await dispatchLogIn(context, { username: payload, password: '' });
            }
        } catch (error) {
            // commitSetLogInError(context, true);
            await dispatchSignupError(context, error);
            router.push('/signup');
        }
    },
    async actionLogIn(context: MainContext, payload: { username: string; password: string }) {
        try {
            if (!toBoolean(designMode)) {
                const response = await api.logInGetToken(payload.username, payload.password);
                const token = response.data.access_token;
                if (token) {
                    saveLocalToken(token);
                    commitSetToken(context, token);
                    commitSetLoggedIn(context, true);
                    commitSetLogInError(context, false);
                    await dispatchGetUserProfile(context);
                    await dispatchRouteLoggedIn(context);
                } else {
                    await dispatchLogOut(context);
                }
            } else {
                await dispatchRouteLoggedIn(context);
            }
        } catch (err) {
            // commitSetLogInError(context, true);
            await dispatchLogOut(context);
            commitAddNotification(context, { content: 'メールアドレスまたはパスワードが不正です', condition: 'error' });
        }
    },
    async actionGetUserProfile(context: MainContext) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: IUserProfile = {
                    id: 0,
                    company_id: 0,
                    first_name: 'CFO',
                    last_name: '太郎',
                    email: 'dummy@gmail.com',
                    is_active: true,
                    is_superuser: false
                }
                data = dummy;
            } else {
                const response = await api.getMe(context.state.token);
                data = response.data;
            }
            if (data) {
                commitSetUserProfile(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateUserProfile(context: MainContext, payload) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.updateMe(context.state.token, payload),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUserProfile(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Profile successfully updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetUserCompany(context: MainContext) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: Company = {
                    id: 0,
                    name: '株式会社CFOAi',
                    created_at: new Date()
                }
                data = dummy;
            } else {
                const response = await api.getMyCompany(context.state.token);
                data = response.data;
            }
            if (data) {
                commitSetUserCompany(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCheckLoggedIn(context: MainContext) {
        if (!context.state.isLoggedIn) {
            if (toBoolean(designMode)) {
                commitSetLoggedIn(context, true);
                await dispatchGetUserProfile(context);
            } else {
                let token = context.state.token;
                if (!token) {
                    const localToken = getLocalToken();
                    if (localToken) {
                        commitSetToken(context, localToken);
                        token = localToken;
                    }
                }
                if (token) {
                    try {
                        const response = await api.getMe(token);
                        commitSetLoggedIn(context, true);
                        commitSetUserProfile(context, response.data);
                    } catch (error) {
                        await dispatchRemoveLogIn(context);
                    }
                } else {
                    await dispatchRemoveLogIn(context);
                }
            }
        }
    },
    async actionCheckSetupCompleted(context: MainContext) {
        try {
            let token = context.state.token;
            const response = await api.getMe(token);
            commitSetUserProfile(context, response.data);

            let isSetupCompleted = response.data.setup_completed_at ? true : false;
            commitSetSetupCompleted(context, isSetupCompleted);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionRemoveLogIn(context: MainContext) {
        removeLocalToken();
        commitSetToken(context, '');
        commitSetLoggedIn(context, false);
        commitSetReports(context, null);
        commitSetReportId(context, null);
    },
    async actionLogOut(context: MainContext) {
        await dispatchRemoveLogIn(context);
        await dispatchRouteLogOut(context);
    },
    async actionUserLogOut(context: MainContext) {
        await dispatchLogOut(context);
    },
    actionRouteLogOut(context: MainContext) {
        if (router.currentRoute.path !== '/login') {
            router.push('/login');
        }
    },
    async actionCheckApiError(context: MainContext, payload: AxiosError) {
        if (payload.response!.status === 401) {
            await dispatchLogOut(context);
        }
    },
    actionRouteLoggedIn(context: MainContext) {
        if (router.currentRoute.path === '/login') {
            if (readHasAdminAccess(context)) {
                router.push('/main/admin');
            } else {
                router.push('/main');
            }
        } else if (router.currentRoute.path === '/signup') {
            router.push('/setup/basic');
        } else if (router.currentRoute.path === '/google-callback') {
            router.push('/main');
        }
    },
    async removeNotification(context: MainContext, payload: { notification: AppNotification, timeout: number }) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                commitRemoveNotification(context, payload.notification);
                resolve(true);
            }, payload.timeout);
        });
    },
    async passwordRecovery(context: MainContext, payload: { user_email: string }) {
        try {
            const response = (await Promise.all([
                api.passwordRecovery(payload.user_email),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitAddNotification(context, { content: 'パスワード再設定メールをお送りしました。<br>メールの内容に従ってパスワードを再設定してください。', condition: 'saved', timeout: 10000});
            await dispatchLogOut(context);
        } catch (error) {
            commitAddNotification(context, { content: 'メールアドレスに誤りがございます', condition: 'error' })
        }
    },
    async passwordReset(context: MainContext, payload: { password: string, token: string }) {
        try {
            const response = (await Promise.all([
                api.passwordReset(payload.password, payload.token),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitAddNotification(context, { content: 'パスワード再登録が完了しました', condition: 'saved' });
            await dispatchLogOut(context);
        } catch (error) {
            commitAddNotification(context, { content: 'パスワード再登録に失敗しました', condition: 'error' });
        }
    },
};

const { dispatch } = getStoreAccessors<MainState | any, State>('');

export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchSignupError = dispatch(actions.actionSignupError);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchCheckSetupCompleted = dispatch(actions.actionCheckSetupCompleted);
export const dispatchGetUserProfile = dispatch(actions.actionGetUserProfile);
export const dispatchGetUserCompany = dispatch(actions.actionGetUserCompany);
export const dispatchSignup = dispatch(actions.actionSignup);
export const dispatchLogIn = dispatch(actions.actionLogIn);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchUpdateUserProfile = dispatch(actions.actionUpdateUserProfile);
export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchPasswordRecovery = dispatch(actions.passwordRecovery);
export const dispatchPasswordReset = dispatch(actions.passwordReset);
export const dispatchGoogleSignup = dispatch(actions.actionGoogleSignup);

